import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiStar, FiHeart, FiAward, FiScissors, FiClock } = FiIcons;

const About = () => {
  const stats = [
    { number: '5+', label: 'Years Experience', icon: FiAward },
    { number: '1000+', label: 'Happy Customers', icon: FiUsers },
    { number: '100%', label: 'Satisfaction Rate', icon: FiStar },
    { number: '7', label: 'Days a Week', icon: FiClock }
  ];

  const values = [
    {
      title: 'Quality Service',
      description: 'We are committed to providing the highest quality grooming services with attention to every detail.',
      icon: FiStar
    },
    {
      title: 'Expert Barbers',
      description: 'Our team consists of skilled professionals with years of experience in men\'s grooming.',
      icon: FiUsers
    },
    {
      title: 'Customer Satisfaction',
      description: 'Your satisfaction is our top priority. We ensure every customer leaves happy and confident.',
      icon: FiHeart
    },
    {
      title: 'Modern Techniques',
      description: 'We stay updated with the latest trends and techniques in the barbering industry.',
      icon: FiScissors
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#826046] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Mr. Shaver</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Your trusted partner in men's grooming, delivering exceptional service and style in the heart of Dubai
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Mr. Shaver Gents Salon was established with a vision to provide premium grooming services 
                to the gentlemen of Dubai. Located in the vibrant area of Al Barsha South, we have been 
                serving our community with dedication and passion.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our salon combines traditional barbering techniques with modern styling trends, ensuring 
                that every client receives a personalized experience that exceeds expectations. We believe 
                that grooming is not just about appearance, but about confidence and self-expression.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At Mr. Shaver, we are committed to maintaining the highest standards of hygiene, 
                professionalism, and customer service. Every visit to our salon is designed to be 
                a relaxing and rejuvenating experience.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/zWmHhJwx/image-1.jpg"
                alt="Salon Interior"
                className="rounded-lg shadow-lg"
              />
              <img
                src="https://i.ibb.co/4wpyrn29/image-2.jpg"
                alt="Professional Service"
                className="rounded-lg shadow-lg mt-8"
              />
              <img
                src="https://i.ibb.co/8LyqXkGv/image-3.jpg"
                alt="Expert Barber"
                className="rounded-lg shadow-lg -mt-8"
              />
              <img
                src="https://i.ibb.co/Z6W7fdm7/image-4.jpg"
                alt="Quality Results"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 bg-[#826046] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Achievements</h2>
            <p className="text-xl max-w-2xl mx-auto">
              Numbers that reflect our commitment to excellence and customer satisfaction
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-sm sm:text-base font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide us in delivering exceptional service to every client
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="bg-[#826046] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Experience the Mr. Shaver Difference
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our family of satisfied customers and discover why we're Dubai's trusted choice for men's grooming
            </p>
            <a
              href="tel:+971501656278"
              className="bg-[#826046] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#6d4d37] transition-colors duration-300 inline-block"
            >
              Call Us Today: +971 50 165 6278
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;